/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, type MemoExoticComponent, type ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import {
	PreviewPanel,
	PreviewPanelBody,
	PreviewPanelHeader,
	PreviewPanelTitle,
} from '@atlassian/preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

type Props = {
	icon: ReactNode;
	name: string;
	url: string;
};

const styles = cssMap({
	body: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		overflowY: 'auto',
		gap: token('space.200'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
	},
	skeleton: {
		display: 'flex',
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
		height: '20px',
	},
	skeletonRow1: {
		width: '100%',
	},
	skeletonRow2: {
		width: '60%',
	},
	skeletonRow3: {
		width: '80%',
	},
	skeletonRow4: {
		width: '40%',
	},
});

export const FallbackPreviewPanel: MemoExoticComponent<
	({ url, name, icon }: Props) => JSX.Element
> = memo(({ url, name, icon }: Props): JSX.Element => {
	const { close } = usePreviewPanelsActions();

	return (
		<PreviewPanel>
			<PreviewPanelHeader onClose={close} url={url}>
				<PreviewPanelTitle title={name} icon={icon} />
			</PreviewPanelHeader>
			<PreviewPanelBody styles={styles.body}>
				<div css={[styles.skeleton, styles.skeletonRow1]}></div>
				<div css={[styles.skeleton, styles.skeletonRow2]}></div>
				<div css={[styles.skeleton, styles.skeletonRow3]}></div>
				<div css={[styles.skeleton, styles.skeletonRow4]}></div>
				<div css={[styles.skeleton, styles.skeletonRow1]}></div>
				<div css={[styles.skeleton, styles.skeletonRow2]}></div>
				<div css={[styles.skeleton, styles.skeletonRow3]}></div>
				<div css={[styles.skeleton, styles.skeletonRow4]}></div>
			</PreviewPanelBody>
		</PreviewPanel>
	);
});
